import dayjs from "dayjs";
import React from "react";
import { useDispatch, useSelector } from "react-redux";

import ExpertResumesLayout from "../../../components/ExpertResumesLayout/ExpertResumesLayout";
import JobRecommended from "../../../components/Jobs/JobRecommended/JobRecommended";
import Loader from "../../../components/Loader";
import Spinner from "../../../components/Spinner";
import { getMyResumesRoutes } from "../../../handlers/navigationItems";
import { compareArray } from "../../../handlers/utilityFunctions";
import dropDownOptions from "../../../seed/dropDownOptions";
import {
	jobSearchStatusOptions,
	jobTypes,
	locationTypes,
	regions,
	seniorityLevels,
	waysToHelpOptions,
} from "../../../seed/job-preferences-data";
import { ResumeDataService } from "../../../services/create-resume/resume-data";

function JobPreferences() {
	const dispatch = useDispatch();
	const [submitting, setSubmitting] = React.useState(false);
	const [loading, setLoading] = React.useState(true);
	const [resumeTitle, setResumeTitle] = React.useState("");
	const [resumeList, setResumeList] = React.useState([]);
	const [errors, setError] = React.useState([]);
	const storedResumes = localStorage.getItem("resumeList");
	const storedJobPreferences = localStorage.getItem("jobPreferences");
	const resumeDataService = React.useMemo(() => new ResumeDataService(), []);
	const expertiseOptions = dropDownOptions.jobExpertiseOptions;
	const [jobPreferences, setJobPreferences] = React.useState({});
	const [persistJobPreferences, setPersistJobPreferences] = React.useState({});
	const data = useSelector((state) => state.SettingsReducer);
	const [jobPreferencesBody, setJobPreferencesBody] = React.useState({});

	function onSuccess(response) {
		if ([...response.data].length === 0) {
			setResumeList([]);
		} else {
			setResumeList(response.data);
			localStorage.setItem("resumeList", JSON.stringify(response.data));
		}
	}

	function onError(error) {
		const errorMessages = error.response.data.message;
		setError(errorMessages);
	}

	const fetchResumeList = React.useCallback(async () => {
		if (storedResumes) {
			setResumeList(JSON.parse(storedResumes));
		} else {
			await resumeDataService.getUserResumes(onSuccess, onError);
		}
	}, [storedResumes, resumeDataService]);

	const onSuccessSettings = React.useCallback(
		(response) => {
			const general = {
				...data.general,
				email: response?.data?.emailAddress,
			};
			const primaryResume = resumeList.filter(
				(item) => item._id === response?.data?.jobPreferences?.primaryResumeReferenceId,
			);
			if (primaryResume.length === 1) {
				const resumeTitle = primaryResume[0]?.journal?.resumeTitle || primaryResume[0]?.heading?.resumeTitle;
				setResumeTitle(resumeTitle);
			}

			dispatch({ type: "EDIT_ GENERAL", payload: general });
			setJobPreferences(response?.data?.jobPreferences);
			setPersistJobPreferences(response?.data?.jobPreferences);
			setJobPreferencesBody({
				emailAddress: response?.data?.emailAddress,
				phoneNumber: data?.general?.phone,
				jobPreferences: {
					primaryResumeReferenceId: response?.data?.jobPreferences?.primaryResumeReferenceId,
					currentJobsearchStatus: response?.data?.jobPreferences?.currentJobsearchStatus,
					waysToHelpme: response?.data?.jobPreferences?.waysToHelpme,
					openForWorkTypes: response?.data?.jobPreferences?.openForWorkTypes,
					areasOfExpertise: Array.isArray(response?.data?.jobPreferences?.areasOfExpertise)
						? response?.data?.jobPreferences?.areasOfExpertise
						: [response?.data?.jobPreferences?.areasOfExpertise],
					locationPreferenceType: response?.data?.jobPreferences?.locationPreferenceType,
					locationsPreferred: response?.data?.jobPreferences?.locationsPreferred,
					seniorityLevel: response?.data?.jobPreferences?.seniorityLevel,
				},
			});
			if (response.data) {
				localStorage.setItem("jobPreferences", JSON.stringify(response?.data));
			}
			setLoading(false);
		},
		[data, dispatch, resumeList],
	);

	const onErrorSettings = React.useCallback(() => {
		window.location.replace("/signin");
	}, []);

	const fetchUsersAccount = React.useCallback(() => {
		setLoading(true);
		if (storedJobPreferences) {
			const storedData = JSON.parse(storedJobPreferences);
			const general = {
				...data.general,
				email: storedData?.emailAddress,
			};
			const primaryResume = resumeList.filter(
				(item) => item._id === storedData?.jobPreferences?.primaryResumeReferenceId,
			);
			if (primaryResume.length === 1) {
				const resumeTitle = primaryResume[0]?.journal?.resumeTitle || primaryResume[0]?.heading?.resumeTitle;
				setResumeTitle(resumeTitle);
			}

			dispatch({ type: "EDIT_ GENERAL", payload: general });
			setJobPreferences(storedData?.jobPreferences);
			setPersistJobPreferences(storedData?.jobPreferences);
			setJobPreferencesBody({
				emailAddress: storedData?.emailAddress,
				phoneNumber: data?.general?.phone,
				jobPreferences: {
					primaryResumeReferenceId: storedData?.jobPreferences?.primaryResumeReferenceId,
					currentJobsearchStatus: storedData?.jobPreferences?.currentJobsearchStatus,
					waysToHelpme: storedData?.jobPreferences?.waysToHelpme,
					openForWorkTypes: storedData?.jobPreferences?.openForWorkTypes,
					areasOfExpertise: Array.isArray(storedData?.jobPreferences?.areasOfExpertise)
						? storedData?.jobPreferences?.areasOfExpertise
						: [storedData?.jobPreferences?.areasOfExpertise],
					locationPreferenceType: storedData?.jobPreferences?.locationPreferenceType,
					locationsPreferred: storedData?.jobPreferences?.locationsPreferred,
					seniorityLevel: storedData?.jobPreferences?.seniorityLevel,
				},
			});
			setLoading(false);
		} else {
			resumeDataService.getUser(onSuccessSettings, onErrorSettings);
		}
	}, [resumeDataService, onSuccessSettings, onErrorSettings, dispatch, data, resumeList, storedJobPreferences]);

	const updateJobPreferences = () => {
		setSubmitting(true);
		resumeDataService.jobPreferences(
			jobPreferencesBody,
			(data) => {
				setJobPreferences(data?.data?.jobPreferences);
				setPersistJobPreferences(data?.data?.jobPreferences);
				localStorage.setItem("jobPreferences", JSON.stringify(data?.data));
				setError([]);
				setSubmitting(false);
			},
			(error) => {
				const errorMessages = error.response.data.message;
				setError(errorMessages);
				setSubmitting(false);
			},
		);
	};

	React.useEffect(() => {
		fetchResumeList();
	}, [fetchResumeList]);

	React.useEffect(() => {
		fetchUsersAccount();
	}, [fetchUsersAccount]);

	return (
		<ExpertResumesLayout navItems={getMyResumesRoutes(dispatch)} headingTitle="Jobs Preferenses" showExpertDetails>
			<div className="my-resumes-section pt-3">
				<div className="container">
					{loading && <Loader />}
					{!loading && (
						<div className="row">
							<div className="col-lg-9 mb-5">
								{errors.length > 0 && (
									<div className="alert alert-danger mb-2">
										<ul className="mb-0">
											{errors.map((error, index) => (
												<li key={index}>{error}</li>
											))}
										</ul>
									</div>
								)}
								<p className="text-md font-medium">Job Preferences</p>
								<p className="text-sm">
									Set your job preferences so we can order jobs more intelligently for you. Once set you can also choose
									to send your preferences to a company during the application process.
								</p>

								<hr className="my-4 w-100" />
								<div className="d-flex justify-content-between flex-wrap">
									<div>
										<p className="text-md font-medium">Current Job Status</p>
										<p className="mb-4 text-sm">Add your current job search status</p>
									</div>
									{persistJobPreferences?.currentJobsearchStatus !== jobPreferences?.currentJobsearchStatus && (
										<div className="mb-4">
											<button className="btn btn-primary rounded-pill" onClick={updateJobPreferences}>
												{submitting ? (
													<div className="flex items-center space-x-4">
														<div>Loading</div>
														<Spinner />
													</div>
												) : (
													"save changes"
												)}
											</button>
										</div>
									)}
								</div>
								<div className="dropdown">
									<button
										className="btn btn-block text-dark bg-white border border-primary rounded-xs px-3 py-2 d-flex justify-content-between"
										type="button"
										id="JobStatusDropdown"
										data-toggle="dropdown"
										aria-haspopup="true"
										aria-expanded="false"
									>
										<span className="text-sm">
											<span
												className={`${
													jobSearchStatusOptions[
														jobPreferences?.currentJobsearchStatus
															? jobSearchStatusOptions.findIndex(
																	(item) => item.value === jobPreferences?.currentJobsearchStatus,
																)
															: 0
													].classIcon
												} mr-3`}
											/>
											{jobPreferences?.currentJobsearchStatus ? jobPreferences?.currentJobsearchStatus : ""}
										</span>
										<span className="fa fa-chevron-down" />
									</button>
									<div className="dropdown-menu w-100" aria-labelledby="JobStatusDropdown">
										{jobSearchStatusOptions.map((item, index) => {
											return (
												<div
													key={index}
													className="dropdown-item cursor-pointer"
													onClick={() => {
														setJobPreferencesBody({
															...jobPreferencesBody,
															jobPreferences: {
																...jobPreferencesBody?.jobPreferences,
																currentJobsearchStatus: item.value,
															},
														});
														setJobPreferences({
															...jobPreferences,
															currentJobsearchStatus: item.value,
														});
													}}
												>
													<div className="d-flex">
														<div className="pr-2">
															<div
																className="rounded-circle bg-light d-flex align-items-center justify-content-center"
																style={{ width: "20px", height: "20px" }}
															>
																<i className={item.classIcon} />
															</div>
														</div>
														<div className="text-wrap">
															<p className="h5">{item.value}</p>
															<p>{item.description}</p>
														</div>
													</div>
												</div>
											);
										})}
									</div>
								</div>

								<hr className="my-4 w-100" />
								<div className="d-flex justify-content-between flex-wrap">
									<div>
										<p className="text-md font-medium">Ways To Help</p>
										<p className="mb-4 text-sm">Tell us, how can Dproz better serve you?</p>
									</div>
									{persistJobPreferences?.waysToHelpme !== jobPreferences?.waysToHelpme && (
										<div className="mb-4">
											<button className="btn btn-primary rounded-pill" onClick={updateJobPreferences}>
												{submitting ? (
													<div className="flex items-center space-x-4">
														<div>Loading</div>
														<Spinner />
													</div>
												) : (
													"save changes"
												)}
											</button>
										</div>
									)}
								</div>
								<div className="dropdown">
									<button
										className="btn btn-block text-dark bg-white border border-primary rounded-xs px-3 py-2 d-flex justify-content-between"
										type="button"
										id="RecommendDropdown"
										data-toggle="dropdown"
										aria-haspopup="true"
										aria-expanded="false"
									>
										<span className="text-sm">
											<span
												className={`${
													waysToHelpOptions[
														jobPreferences?.waysToHelpme
															? waysToHelpOptions.findIndex((item) => item.value === jobPreferences?.waysToHelpme)
															: 0
													].classIcon
												} mr-3`}
											/>
											{jobPreferences?.waysToHelpme ? jobPreferences?.waysToHelpme : ""}
										</span>
										<span className="fa fa-chevron-down" />
									</button>
									<div className="dropdown-menu w-100" aria-labelledby="RecommendDropdown">
										{waysToHelpOptions.map((item, index) => {
											return (
												<div
													key={index}
													className="dropdown-item cursor-pointer"
													onClick={() => {
														setJobPreferencesBody({
															...jobPreferencesBody,
															jobPreferences: {
																...jobPreferencesBody?.jobPreferences,
																waysToHelpme: item.value,
															},
														});
														setJobPreferences({
															...jobPreferences,
															waysToHelpme: item.value,
														});
													}}
												>
													<div className="d-flex">
														<div className="pr-2">
															<div
																className="rounded-circle bg-light d-flex align-items-center justify-content-center"
																style={{ width: "20px", height: "20px" }}
															>
																<i className={item.classIcon} />
															</div>
														</div>
														<div className="text-wrap">
															<p className="h5">{item.value}</p>
															<p>{item.description}</p>
														</div>
													</div>
												</div>
											);
										})}
									</div>
								</div>

								<hr className="my-4 w-100" />
								<div className="d-flex justify-content-between flex-wrap">
									<div>
										<p className="text-md font-medium">Select Primary Resume</p>
										<p className="mb-4 text-sm">
											Link a primary Resume/CV to determine fit with connected opportunities
										</p>
									</div>
									{persistJobPreferences?.primaryResumeReferenceId !== jobPreferences?.primaryResumeReferenceId && (
										<div className="mb-4">
											<button className="btn btn-primary rounded-pill" onClick={updateJobPreferences}>
												{submitting ? (
													<div className="flex items-center space-x-4">
														<div>Loading</div>
														<Spinner />
													</div>
												) : (
													"save changes"
												)}
											</button>
										</div>
									)}
								</div>
								<div className="dropdown">
									<button
										className="btn btn-block text-dark bg-white border border-primary rounded-xs px-3 py-2 d-flex justify-content-between"
										type="button"
										id="ResumeDropdown"
										data-toggle="dropdown"
										aria-haspopup="true"
										aria-expanded="false"
									>
										<span className="text-sm">{resumeTitle}</span>
										<span className="fa fa-chevron-down" />
									</button>
									<div className="dropdown-menu w-100" aria-labelledby="ResumeDropdown">
										{resumeList.map((item, index) => {
											const resumeTitle = item.journal.resumeTitle || item.heading.resumeTitle;

											return (
												<div
													key={index}
													className="dropdown-item cursor-pointer"
													onClick={() => {
														const resumeTitle = item.journal.resumeTitle || item.heading.resumeTitle;
														const resumeId = item._id;
														setResumeTitle(`${resumeTitle}`);
														setJobPreferencesBody({
															...jobPreferencesBody,
															jobPreferences: {
																...jobPreferencesBody?.jobPreferences,
																primaryResumeReferenceId: resumeId,
															},
														});
														setJobPreferences({
															...jobPreferences,
															primaryResumeReferenceId: resumeId,
														});
													}}
												>
													<div className="pr-2">
														<p>
															{`${resumeTitle} - Updated on ${dayjs(item.journal.lastUpdatedOn).format(
																"DD MMMM, YYYY",
															)}`}
														</p>
													</div>
												</div>
											);
										})}
									</div>
								</div>

								<hr className="my-4 w-100" />
								<div className="d-flex justify-content-between flex-wrap">
									<div>
										<p className="text-md font-medium">Job Types</p>
										<p className="mb-4 text-sm">Add employment or engagement type(s) are you open for</p>
									</div>
									{compareArray(persistJobPreferences?.openForWorkTypes, jobPreferences?.openForWorkTypes) && (
										<div className="mb-4">
											<button className="btn btn-primary rounded-pill" onClick={updateJobPreferences}>
												{submitting ? (
													<div className="flex items-center space-x-4">
														<div>Loading</div>
														<Spinner />
													</div>
												) : (
													"save changes"
												)}
											</button>
										</div>
									)}
								</div>
								<div className="row pl-3">
									{jobTypes.map((item, index) => {
										return (
											<div key={index} className="col-md-6 d-flex align-items-center mb-2">
												<input
													type="checkbox"
													style={{ width: 24, height: 24, transform: "scale(1.2)", cursor: "pointer" }}
													id={`job-${index}`}
													checked={jobPreferences?.openForWorkTypes?.includes(item)}
													onChange={() => {
														setJobPreferencesBody({
															...jobPreferencesBody,
															jobPreferences: {
																...jobPreferencesBody?.jobPreferences,
																openForWorkTypes: !jobPreferencesBody?.jobPreferences?.openForWorkTypes
																	? [item]
																	: jobPreferences?.openForWorkTypes?.includes(item)
																		? jobPreferencesBody?.jobPreferences?.openForWorkTypes.filter(
																				(jobType) => jobType !== item,
																			)
																		: [...jobPreferencesBody.jobPreferences.openForWorkTypes, item],
															},
														});
														setJobPreferences({
															...jobPreferences,
															openForWorkTypes: !jobPreferencesBody?.jobPreferences?.openForWorkTypes
																? [item]
																: jobPreferences?.openForWorkTypes?.includes(item)
																	? jobPreferencesBody?.jobPreferences?.openForWorkTypes.filter(
																			(jobType) => jobType !== item,
																		)
																	: [...jobPreferencesBody.jobPreferences.openForWorkTypes, item],
														});
													}}
												/>
												<label className="form-check-label ml-2 text-sm" htmlFor={`job-${index}`}>
													{item}
												</label>
											</div>
										);
									})}
								</div>

								<hr className="my-4 w-100" />
								<div className="d-flex justify-content-between flex-wrap">
									<div>
										<p className="text-md font-medium">Seniority Level</p>
										<p className="mb-4 text-sm">Add the seniority or experperince level you are targeting</p>
									</div>
									{persistJobPreferences?.seniorityLevel !== jobPreferences?.seniorityLevel && (
										<div className="mb-4">
											<button className="btn btn-primary rounded-pill" onClick={updateJobPreferences}>
												{submitting ? (
													<div className="flex items-center space-x-4">
														<div>Loading</div>
														<Spinner />
													</div>
												) : (
													"save changes"
												)}
											</button>
										</div>
									)}
								</div>
								<div className="dropdown">
									<button
										className="btn btn-block text-dark bg-white border border-primary rounded-xs px-3 py-2 d-flex justify-content-between"
										type="button"
										id="SeniorityLevelDropdown"
										data-toggle="dropdown"
										aria-haspopup="true"
										aria-expanded="false"
									>
										<span className="text-sm">
											{jobPreferences?.seniorityLevel ? jobPreferences?.seniorityLevel : ""}
										</span>
										<span className="fa fa-chevron-down" />
									</button>
									<div className="dropdown-menu w-100" aria-labelledby="SeniorityLevelDropdown">
										{seniorityLevels.map((item, index) => {
											return (
												<div
													key={index}
													className="dropdown-item cursor-pointer"
													onClick={() => {
														setJobPreferencesBody({
															...jobPreferencesBody,
															jobPreferences: {
																...jobPreferencesBody?.jobPreferences,
																seniorityLevel: item.value,
															},
														});
														setJobPreferences({
															...jobPreferences,
															seniorityLevel: item.value,
														});
													}}
												>
													<p>{item.value}</p>
												</div>
											);
										})}
									</div>
								</div>

								<hr className="my-4 w-100" />
								<div className="d-flex justify-content-between flex-wrap">
									<div>
										<p className="text-md font-medium">Location Types</p>
										<p className="mb-4 text-sm">Indicate whether you are open to work anywehere</p>
									</div>
									{persistJobPreferences?.locationPreferenceType !== jobPreferences?.locationPreferenceType && (
										<div className="mb-4">
											<button className="btn btn-primary rounded-pill" onClick={updateJobPreferences}>
												{submitting ? (
													<div className="flex items-center space-x-4">
														<div>Loading</div>
														<Spinner />
													</div>
												) : (
													"save changes"
												)}
											</button>
										</div>
									)}
								</div>
								<div className="dropdown">
									<button
										className="btn btn-block text-dark bg-white border border-primary rounded-xs px-3 py-2 d-flex justify-content-between"
										type="button"
										id="LocationTypesDropdown"
										data-toggle="dropdown"
										aria-haspopup="true"
										aria-expanded="false"
									>
										<span className="text-sm">
											<span
												className={`${
													locationTypes[
														jobPreferences?.locationPreferenceType
															? locationTypes.findIndex((item) => item.value === jobPreferences?.locationPreferenceType)
															: 0
													].classIcon
												} mr-3`}
											/>
											{jobPreferences?.locationPreferenceType ? jobPreferences?.locationPreferenceType : ""}
										</span>
										<span className="fa fa-chevron-down" />
									</button>
									<div className="dropdown-menu w-100" aria-labelledby="LocationTypesDropdown">
										{locationTypes.map((item, index) => {
											return (
												<div
													key={index}
													className="dropdown-item cursor-pointer"
													onClick={() => {
														setJobPreferencesBody({
															...jobPreferencesBody,
															jobPreferences: {
																...jobPreferencesBody?.jobPreferences,
																locationPreferenceType: item.value,
															},
														});
														setJobPreferences({
															...jobPreferences,
															locationPreferenceType: item.value,
														});
													}}
												>
													<div className="d-flex">
														<div className="pr-2">
															<div
																className="rounded-circle bg-light d-flex align-items-center justify-content-center"
																style={{ width: "20px", height: "20px" }}
															>
																<i className={item.classIcon} />
															</div>
														</div>
														<div className="text-wrap">
															<p className="h5">{item.value}</p>
															<p>{item.description}</p>
														</div>
													</div>
												</div>
											);
										})}
									</div>
								</div>

								{jobPreferences?.locationPreferenceType === locationTypes[1].value && (
									<>
										<hr className="my-4 w-100" />
										<div className="d-flex justify-content-between flex-wrap">
											<div>
												<p className="text-lg font-medium">Regions</p>
												<p className="mb-4 text-sm">Add preferred work locations</p>
											</div>
											{compareArray(persistJobPreferences?.locationsPreferred, jobPreferences?.locationsPreferred) && (
												<div className="mb-4">
													<button className="btn btn-primary rounded-pill" onClick={updateJobPreferences}>
														{submitting ? (
															<div className="flex items-center space-x-4">
																<div>Loading</div>
																<Spinner />
															</div>
														) : (
															"save changes"
														)}
													</button>
												</div>
											)}
										</div>
										<div className="row pl-3">
											{regions.map((item, index) => {
												return (
													<div key={index} className="col-md-4 d-flex align-items-center mb-2">
														<input
															type="checkbox"
															style={{ width: 24, height: 24, transform: "scale(1.2)", cursor: "pointer" }}
															id={`region-${index}`}
															checked={jobPreferences?.locationsPreferred?.includes(item)}
															onChange={() => {
																setJobPreferencesBody({
																	...jobPreferencesBody,
																	jobPreferences: {
																		...jobPreferencesBody?.jobPreferences,
																		locationsPreferred: !jobPreferencesBody?.jobPreferences?.locationsPreferred
																			? [item]
																			: jobPreferences?.locationsPreferred?.includes(item)
																				? jobPreferencesBody?.jobPreferences?.locationsPreferred.filter(
																						(region) => region !== item,
																					)
																				: [...jobPreferencesBody.jobPreferences.locationsPreferred, item],
																	},
																});
																setJobPreferences({
																	...jobPreferences,
																	locationsPreferred: !jobPreferencesBody?.jobPreferences?.locationsPreferred
																		? [item]
																		: jobPreferences?.locationsPreferred?.includes(item)
																			? jobPreferencesBody?.jobPreferences?.locationsPreferred.filter(
																					(region) => region !== item,
																				)
																			: [...jobPreferencesBody.jobPreferences.locationsPreferred, item],
																});
															}}
														/>
														<label className="form-check-label ml-2 text-sm" htmlFor={`region-${index}`}>
															{item}
														</label>
													</div>
												);
											})}
										</div>
									</>
								)}

								<hr className="my-4 w-100" />
								<div className="d-flex justify-content-between flex-wrap">
									<div>
										<p className="text-md font-medium">Areas Of Expertise</p>
										<p className="mb-4 text-sm">
											Add areas of your expertise to get connected with relavant opportunities
										</p>
									</div>
									{compareArray(persistJobPreferences?.areasOfExpertise, jobPreferences?.areasOfExpertise) && (
										<div className="mb-4">
											<button className="btn btn-primary rounded-pill" onClick={updateJobPreferences}>
												{submitting ? (
													<div className="flex items-center space-x-4">
														<div>Loading</div>
														<Spinner />
													</div>
												) : (
													"save changes"
												)}
											</button>
										</div>
									)}
								</div>
								<div className="row pl-3">
									{expertiseOptions.map((item, index) => {
										return (
											<div key={index} className="col-md-4 d-flex align-items-center mb-2">
												<input
													type="checkbox"
													style={{ width: 24, height: 24, transform: "scale(1.2)", cursor: "pointer" }}
													id={`expertise-${index}`}
													checked={jobPreferences?.areasOfExpertise?.includes(item)}
													onChange={() => {
														setJobPreferencesBody({
															...jobPreferencesBody,
															jobPreferences: {
																...jobPreferencesBody?.jobPreferences,
																areasOfExpertise: !jobPreferencesBody?.jobPreferences?.areasOfExpertise
																	? [item]
																	: jobPreferences?.areasOfExpertise?.includes(item)
																		? jobPreferencesBody?.jobPreferences?.areasOfExpertise.filter(
																				(expertise) => expertise !== item,
																			)
																		: [...jobPreferencesBody.jobPreferences.areasOfExpertise, item],
															},
														});
														setJobPreferences({
															...jobPreferences,
															areasOfExpertise: !jobPreferencesBody?.jobPreferences?.areasOfExpertise
																? [item]
																: jobPreferences?.areasOfExpertise?.includes(item)
																	? jobPreferencesBody?.jobPreferences?.areasOfExpertise.filter(
																			(expertise) => expertise !== item,
																		)
																	: [...jobPreferencesBody.jobPreferences.areasOfExpertise, item],
														});
													}}
												/>
												<label className="form-check-label ml-2 text-sm" htmlFor={`expertise-${index}`}>
													{item}
												</label>
											</div>
										);
									})}
								</div>
							</div>
							<JobRecommended />
						</div>
					)}
				</div>
			</div>
		</ExpertResumesLayout>
	);
}

export default JobPreferences;
